import type { ThemeOptions } from '@mui/material/styles';

export const fontFamily = '"Inter Variable", "Inter", "Helvetica", "Arial", sans-serif';

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

/**
 * Material UI has no fontWeightSemiBold, but the Figma library declares one and
 * SurePass uses 600 for emphasis, so it is added to the theme rather than left
 * as a loose literal.
 */
declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontWeightSemiBold: number;
  }
  interface TypographyVariantsOptions {
    fontWeightSemiBold?: number;
  }
}

/**
 * The heading ramp, shifted down three steps from Material UI's.
 *
 * MUI's scale runs 96 / 60 / 48 / 34 / 24 / 20, which is a marketing-page ramp:
 * a 96px h1 is a hero, and SurePass is an application. So each level takes the
 * values of the one three steps below it — `h1` is the old `h4`, `h2` the old
 * `h5`, `h3` the old `h6` — and the three oversized steps are gone.
 *
 * **`h4`–`h6` resolve to the `h3` step rather than continuing down.** Nothing
 * in the system uses them: a page title is `h1`, a section is `h2`, a card is
 * `h3`, and that is every heading role there is. Carrying them further down
 * would mean 14px headings, which is smaller than body copy and unreadable —
 * so a heading nested past the third level stops shrinking instead. They exist
 * because Material UI always ships six, not because SurePass has six.
 *
 * Letter-spacing stays `normal` throughout, as it already was at every step.
 */
const headings = {
  h1: { fontSize: '2.125rem', fontWeight: fontWeights.regular, lineHeight: 1.235 },
  h2: { fontSize: '1.5rem', fontWeight: fontWeights.regular, lineHeight: 1.334 },
  h3: { fontSize: '1.25rem', fontWeight: fontWeights.regular, lineHeight: 1.6 },
} as const;

/**
 * Only the steps where SurePass diverges from MUI 9's defaults. Beyond the
 * heading ramp above, the deltas are the family, the weights, and body2's
 * tracking — the other sizes already match the Figma desktop scale.
 */
/**
 * Button label steps beyond Material UI's three, carried in the Figma library
 * as typography/buttonXL and typography/buttonXXL. They live only inside the
 * xlarge and xxlarge button sizes, so they are plain style objects rather than
 * Typography variants — there is no `<Typography variant="buttonXL">`.
 */
export const buttonSizeTypography = {
  xlarge: { fontSize: '1.5625rem', lineHeight: 32 / 25, letterSpacing: '0.46px' },
  xxlarge: { fontSize: '2.125rem', lineHeight: 40 / 34, letterSpacing: '0.46px' },
} as const;

/**
 * `typography/overlineL` from the Figma partner-integration file: the overline
 * at 14px semibold rather than 12px regular. It appears only inside the OCID
 * card — the agency line, the status chip and the designation bar — so it is a
 * plain style object rather than a Typography variant, exactly as
 * `buttonSizeTypography` is. There is no `<Typography variant="overlineL">`.
 *
 * The agency line is Figma's `overlineL-paragraph`, the same style at
 * `lineHeight: 1.2` so a long department name wraps to two tight lines.
 */
export const overlineLarge = {
  fontSize: '0.875rem',
  fontWeight: fontWeights.semiBold,
  letterSpacing: '1px',
  lineHeight: 2.66,
  textTransform: 'uppercase',
} as const;

/**
 * Component text styles from the Figma library, on the same footing as
 * `overlineLarge` and `buttonSizeTypography`: each belongs to exactly one
 * component, so they are style objects rather than Typography variants — there
 * is no `<Typography variant="chipLabel">`.
 *
 * Material UI hardcodes a size on each of these components instead of spreading
 * a variant, so without this they silently keep MUI's numbers — `tooltip/label`
 * worst of all, at 11px Medium where the library says 10px Semi Bold. Every one
 * names the Figma style it came from.
 *
 * `list/subheader` is deliberately not here: `MuiListSubheader` already spreads
 * `subtitle2` and strips Figma's 48px leading, because that number is a row
 * height rather than a line height. See the note on it in `theme/index.ts`.
 */
export const componentTypography = {
  /** Figma `chip/label` — 13 Reg, lh 18, ls 0.16. */
  chipLabel: {
    fontSize: '0.8125rem',
    fontWeight: fontWeights.regular,
    lineHeight: 18 / 13,
    letterSpacing: '0.16px',
  },
  /** Figma `badge/label` — 12 SB, lh 20, ls 0.14. */
  badgeLabel: {
    fontSize: '0.75rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 20 / 12,
    letterSpacing: '0.14px',
  },
  /** Figma `tooltip/label` — 10 SB, lh 14, ls 0. */
  tooltipLabel: {
    fontSize: '0.625rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 14 / 10,
    letterSpacing: 0,
  },
  /** Figma `avatar/initialsLg` — 20 Reg, lh 20, ls 0.14. The Md and Sm cuts
   *  are not ported: `Avatar` has no size prop to hang them on, so a smaller
   *  avatar is sized through `sx` and its initials scale with it. */
  avatarInitials: {
    fontSize: '1.25rem',
    fontWeight: fontWeights.regular,
    lineHeight: 1,
    letterSpacing: '0.14px',
  },
} as const;

export const typography: ThemeOptions['typography'] = {
  fontFamily,
  fontWeightLight: fontWeights.light,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightSemiBold: fontWeights.semiBold,
  fontWeightBold: fontWeights.bold,
  ...headings,
  // The three levels below h3 hold the h3 step; see the note above.
  h4: headings.h3,
  h5: headings.h3,
  h6: headings.h3,
  subtitle1: { fontWeight: fontWeights.semiBold },
  subtitle2: { fontWeight: fontWeights.semiBold },
  // textTransform belongs here rather than on MuiButton: Fab, ToggleButton and
  // ButtonGroup all spread typography.button, so overriding one component would
  // leave the others uppercasing.
  button: { fontWeight: fontWeights.semiBold, textTransform: 'none' },
  body2: { letterSpacing: '0.17px' },
};
